import Vue from 'vue'
import Vuex from 'vuex'
import cart from './modules/cart'
import billing from './modules/billing'

Vue.use(Vuex)

const isTokenExpired = (token) => {
  try {
    const payload = token.split('.')[1]
    if (!payload) return true

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/')
    const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=')
    const decodedPayload = JSON.parse(atob(paddedPayload))
    if (!decodedPayload.exp) return false

    return decodedPayload.exp * 1000 <= Date.now()
  } catch (error) {
    return true
  }
}

export default new Vuex.Store({
  state: {
    auth: {
      isAuthenticated: false,
      user: null,
      token: null
    },
    website: {
      websites: [],
      currentWebsite: null,
      loading: false
    },
    subscription: {
      plan: null,
      limits: {
        maxWebsites: 0,
        maxStorageGb: 0
      },
      usage: {
        websites: 0,
        storageGb: 0
      },
      modules: [],
      loading: false
    }
  },
  getters: {
    isAuthenticated: state => state.auth.isAuthenticated,
    currentUser: state => state.auth.user,
    token: state => state.auth.token,
    // Website getters
    websites: state => state.website.websites,
    currentWebsite: state => state.website.currentWebsite,
    websitesLoading: state => state.website.loading,
    // Subscription getters
    currentPlan: state => state.subscription.plan,
    subscriptionLimits: state => state.subscription.limits,
    subscriptionUsage: state => state.subscription.usage,
    activeModules: state => state.subscription.modules,
    isModuleActive: state => moduleName => state.subscription.modules.includes(moduleName),
    canCreateWebsite: state => state.subscription.usage.websites < state.subscription.limits.maxWebsites,
    storageUsagePercent: state => {
      if (state.subscription.limits.maxStorageGb === 0) return 0
      return (state.subscription.usage.storageGb / state.subscription.limits.maxStorageGb) * 100
    },
    websiteUsagePercent: state => {
      if (state.subscription.limits.maxWebsites === 0) return 0
      return (state.subscription.usage.websites / state.subscription.limits.maxWebsites) * 100
    }
  },
  mutations: {
    SET_AUTH(state, { isAuthenticated, user, token }) {
      state.auth.isAuthenticated = isAuthenticated
      state.auth.user = user
      state.auth.token = token
    },
    UPDATE_USER(state, user) {
      state.auth.user = { ...state.auth.user, ...user }
    },
    CLEAR_AUTH(state) {
      state.auth.isAuthenticated = false
      state.auth.user = null
      state.auth.token = null
    },
    // Website mutations
    SET_WEBSITES(state, websites) {
      state.website.websites = websites
    },
    SET_CURRENT_WEBSITE(state, website) {
      state.website.currentWebsite = website
    },
    SET_WEBSITES_LOADING(state, loading) {
      state.website.loading = loading
    },
    ADD_WEBSITE(state, website) {
      state.website.websites.push(website)
    },
    UPDATE_WEBSITE(state, updatedWebsite) {
      const index = state.website.websites.findIndex(w => w.id === updatedWebsite.id)
      if (index !== -1) {
        Vue.set(state.website.websites, index, updatedWebsite)
      }
      if (state.website.currentWebsite && state.website.currentWebsite.id === updatedWebsite.id) {
        state.website.currentWebsite = updatedWebsite
      }
    },
    REMOVE_WEBSITE(state, websiteId) {
      state.website.websites = state.website.websites.filter(w => w.id !== websiteId)
      if (state.website.currentWebsite && state.website.currentWebsite.id === websiteId) {
        state.website.currentWebsite = state.website.websites[0] || null
      }
    },
    // Subscription mutations
    SET_SUBSCRIPTION_INFO(state, data) {
      state.subscription.plan = data.plan
      state.subscription.limits = data.limits
      state.subscription.usage = data.usage
      state.subscription.modules = data.modules
    },
    SET_SUBSCRIPTION_LOADING(state, loading) {
      state.subscription.loading = loading
    },
    INCREMENT_WEBSITE_COUNT(state) {
      state.subscription.usage.websites++
    },
    DECREMENT_WEBSITE_COUNT(state) {
      if (state.subscription.usage.websites > 0) {
        state.subscription.usage.websites--
      }
    },
    CLEAR_SUBSCRIPTION(state) {
      state.subscription.plan = null
      state.subscription.limits = { maxWebsites: 0, maxStorageGb: 0 }
      state.subscription.usage = { websites: 0, storageGb: 0 }
      state.subscription.modules = []
    }
  },
  actions: {
    loginSuccess({ commit }, { user, token }) {
      commit('SET_AUTH', {
        isAuthenticated: true,
        user,
        token
      })
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
    },
    logout({ commit }) {
      commit('CLEAR_AUTH')
      commit('SET_WEBSITES', [])
      commit('SET_CURRENT_WEBSITE', null)
      commit('CLEAR_SUBSCRIPTION')
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('currentWebsiteId')
    },
    initializeAuth({ commit }) {
      const token = localStorage.getItem('token')
      const userRaw = localStorage.getItem('user')
      const user = userRaw ? JSON.parse(userRaw) : null
      
      if (token && user && !isTokenExpired(token)) {
        commit('SET_AUTH', {
          isAuthenticated: true,
          user,
          token
        })
      } else {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('currentWebsiteId')
      }
    },
    // Website actions
    async loadWebsites({ commit }) {
      commit('SET_WEBSITES_LOADING', true)
      try {
        const WebsiteService = await import('@/services/website')
        const response = await WebsiteService.default.getAllByUser()
        commit('SET_WEBSITES', response.websites)
        
        // Auto-seleccionar el primer sitio web si no hay uno seleccionado
        const currentWebsiteId = localStorage.getItem('currentWebsiteId')
        let websiteToSelect = null
        
        if (currentWebsiteId) {
          websiteToSelect = response.websites.find(w => w.id === parseInt(currentWebsiteId))
        }
        
        if (!websiteToSelect && response.websites.length > 0) {
          websiteToSelect = response.websites[0]
        }
        
        if (websiteToSelect) {
          commit('SET_CURRENT_WEBSITE', websiteToSelect)
          localStorage.setItem('currentWebsiteId', websiteToSelect.id.toString())
        }
        
        return response.websites
      } catch (error) {
        console.error('Error loading websites:', error)
        throw error
      } finally {
        commit('SET_WEBSITES_LOADING', false)
      }
    },
    selectWebsite({ commit }, website) {
      commit('SET_CURRENT_WEBSITE', website)
      if (website) {
        localStorage.setItem('currentWebsiteId', website.id.toString())
      } else {
        localStorage.removeItem('currentWebsiteId')
      }
    },
    addWebsite({ commit }, website) {
      commit('ADD_WEBSITE', website)
    },
    updateWebsite({ commit }, website) {
      commit('UPDATE_WEBSITE', website)
    },
    removeWebsite({ commit }, websiteId) {
      commit('REMOVE_WEBSITE', websiteId)
    },
    // Subscription actions
    async loadSubscriptionInfo({ commit }) {
      commit('SET_SUBSCRIPTION_LOADING', true)
      try {
        const SubscriptionService = await import('@/services/subscription')
        const data = await SubscriptionService.default.getSubscriptionInfo()
        commit('SET_SUBSCRIPTION_INFO', data)
        return data
      } catch (error) {
        console.error('Error loading subscription info:', error)
        // En caso de error, limpiar la suscripción
        commit('CLEAR_SUBSCRIPTION')
        throw error
      } finally {
        commit('SET_SUBSCRIPTION_LOADING', false)
      }
    },
    // User profile actions
    updateUserProfile({ commit }, user) {
      commit('UPDATE_USER', user)
      // Actualizar también en localStorage
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
      const updatedUser = { ...currentUser, ...user }
      localStorage.setItem('user', JSON.stringify(updatedUser))
    }
  },
  modules: {
    cart,
    billing
  }
})
